import { Box, Divider } from "@mui/material";
import { throttle } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SolidGradientColorPicker from "../../../../../../../components/common/SolidGradientColorPicker/SolidGradientColorPicker";
import { useAppStore } from "../../../../../../../hooks/useStores";
import useDesignEditorUtils, { FeatureType, Property } from "../../../../../../CustomUtils/UseDesignEditor";
import StepperNavigation from "../../../../../../StepperNavigation/StepperNavigation";
import MainTabHeader from "../../../../../../components/common/MainTabHeader/MainTabHeader";
import { DesignEditorContext } from "../../../../../../contexts/DesignEditor";
import { useActiveObject } from "../../../../../../hooks/useActiveObject";
import UseBrandConfigs from "../../../../../../hooks/useBrandConfigs";
import { useEditor } from "../../../../../../hooks/useEditor";
import { IStaticText, LayerType } from "../../../../../../types";
import { getTextProperties } from "../../../../utils/text";
import SelectedBrandColors from "../Common/SelectedBrandColors/SelectedBrandColors";
import styles from "./TextFill.module.scss";

const TextFill = () => {
	const { t: tEditorTabs } = useTranslation("translation", { keyPrefix: "editor.tabs" });
	const { isPopupView } = useContext(DesignEditorContext);
	const editor = useEditor();
	const { brandFontStore, campaignStore } = useAppStore();
	const activeObject = useActiveObject() as Required<IStaticText>;
	const { modifyActiveObjectProperty } = UseBrandConfigs();
	const activeObjectColor = activeObject?.fill;
	const [color, setColor] = useState(activeObjectColor);
	const { EditAllAds, getRgbAndOpacityFromRgba, updateColorOpacity } = useDesignEditorUtils();

	useEffect(() => {
		if (editor) {
			if (activeObject && activeObject.type === "StaticText") {
				const textProperties = getTextProperties(activeObject, brandFontStore?.brandOrgFonts);

				setColor(updateColorOpacity(textProperties.color, activeObject.opacity));
				if (activeObject) {
					activeObject.fill = textProperties.color;
				}
			}
		}
	}, [editor, activeObject]);

	const updateObjectFill = throttle((color: string, brandColor: boolean = false) => {
		if (activeObject) {
			const result = getRgbAndOpacityFromRgba(color);

			editor?.objects.update(
				{
					fill: result?.rgb ?? color,
					opacity: brandColor ? undefined : (result?.opacity ?? 1),
					brandId: undefined,
				},
				undefined,
				!campaignStore.isEditingAllAds,
			);

			EditAllAds(LayerType.STATIC_TEXT, activeObject.textType, FeatureType.COLOR, color);
		}
	}, 100);

	return (
		<>
			<Box className={styles.container}>
				<MainTabHeader title={tEditorTabs("fillColor")} />
				<Box className={`${styles.tabs} ${styles.fullHeight}`}>
					<Box className={styles.ColorFillContent}>
						<Box className={styles.colorPickerContainer}>
							<SolidGradientColorPicker
								color={color}
								onChange={(color: string) => {
									setColor(color);
									updateObjectFill(color);
									modifyActiveObjectProperty(activeObject, Property.COLOR_NUMBER, undefined);
								}}
								hideColorTypeBtns={true}
							/>
						</Box>
						<Divider className={styles.mainDivider} />
						<SelectedBrandColors
							activeObject={activeObject}
							updateColor={(color: string) => {
								updateObjectFill(color, true);
							}}
							property={Property.COLOR_NUMBER}
						/>
					</Box>
				</Box>
				{isPopupView && <StepperNavigation />}
			</Box>
		</>
	);
};

export default TextFill;
